import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getApiBaseUrl } from "../utils/apiConfig";
import { isDevelopment } from "../utils/config";

const DevTools = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const [config, setConfig] = useState({
    memberCount: 50,
    channelCount: 20,
    messageCount: 100,
    spreadHours: 24,
    userGroupCount: 5,
    generateDirectory: true,
    realisticTopicDistribution: true,
    dominantTopicShare: 0.45,
  });

  useEffect(() => {
    if (!isDevelopment()) {
      navigate("/dashboard");
      return;
    }

    const userData = localStorage.getItem("user");
    if (userData && userData !== "undefined") {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        if (parsedUser.organizations && parsedUser.organizations.length > 0) {
          const orgId = parsedUser.organizations[0].organizationId;
          fetchOrganization(orgId);
        }
      } catch (err) {
        console.error("Error parsing user data:", err);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchOrganization = async (organizationId) => {
    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/organization/${organizationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setOrganization(data);
    } catch (err) {
      console.error("Error fetching organization:", err);
    }
  };

  const handleGenerate = async () => {
    const orgId = organization?._id || organization?.id;
    if (!orgId) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const res = await fetch(`${apiUrl}/dev-tools/generate-events`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ organizationId: orgId, ...config }),
      });

      const data = await res.json();
      if (data.success) {
        setResult(data);
      } else {
        setError(data.message || "Failed to generate events");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: Number(value) }));
  };

  const handleToggle = (field) => {
    setConfig((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleChangeFloat = (field, value) => {
    const num = Number(value);
    setConfig((prev) => ({ ...prev, [field]: Number.isFinite(num) ? num : prev[field] }));
  };

  return (
    <div className="h-screen h-dvh overflow-hidden flex flex-row bg-bg-gradient-dash">
      <Sidebar user={user} organization={organization} organizations={[]} />
      <div className="flex-1 overflow-y-auto flex flex-col items-center">
        <div className="w-full max-w-[768px] flex flex-col gap-6">

          {/* Header */}
          <div className="flex flex-col gap-1 pt-4">
            <h1 className="font-schibsted font-medium text-[20px] leading-6 text-black-0">Dev Tools</h1>
            <p className="font-schibsted font-normal text-[12px] leading-5 text-grey-60">
              Staging-only tools for generating test data. Not available in production.
            </p>
          </div>

          {/* Mock Data Generator */}
          <div className="flex flex-col gap-4 rounded-xl border border-[#e7ebee] bg-white p-5">
            <div className="flex flex-col gap-1">
              <p className="font-schibsted font-semibold text-[16px] leading-6 text-black-0">
                Mock EventBuffer Generator
              </p>
              <p className="font-schibsted font-normal text-[12px] leading-5 text-grey-60">
                Generate fake Slack messages that are injected into the database as real event buffers.
                They will be processed by the AI pipeline and deleted after expiry, just like real events.
              </p>
            </div>

            {/* Configuration inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="font-schibsted font-medium text-[13px] text-black-0">
                  Members
                </label>
                <input
                  type="number"
                  min={1}
                  max={200}
                  value={config.memberCount}
                  onChange={(e) => handleChange("memberCount", e.target.value)}
                  className="h-9 px-3 rounded-lg border border-grey-30 font-schibsted text-[14px] text-black-0 focus:outline-none focus:border-grey-40"
                />
                <span className="font-schibsted text-[11px] text-grey-60">Fake Slack user IDs (max 200)</span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-schibsted font-medium text-[13px] text-black-0">
                  Channels
                </label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={config.channelCount}
                  onChange={(e) => handleChange("channelCount", e.target.value)}
                  className="h-9 px-3 rounded-lg border border-grey-30 font-schibsted text-[14px] text-black-0 focus:outline-none focus:border-grey-40"
                />
                <span className="font-schibsted text-[11px] text-grey-60">Fake Slack channels (max 50)</span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-schibsted font-medium text-[13px] text-black-0">
                  Messages
                </label>
                <input
                  type="number"
                  min={1}
                  max={5000}
                  value={config.messageCount}
                  onChange={(e) => handleChange("messageCount", e.target.value)}
                  className="h-9 px-3 rounded-lg border border-grey-30 font-schibsted text-[14px] text-black-0 focus:outline-none focus:border-grey-40"
                />
                <span className="font-schibsted text-[11px] text-grey-60">Total event buffers to generate (max 5000)</span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-schibsted font-medium text-[13px] text-black-0">
                  Spread (hours)
                </label>
                <input
                  type="number"
                  min={1}
                  max={168}
                  value={config.spreadHours}
                  onChange={(e) => handleChange("spreadHours", e.target.value)}
                  className="h-9 px-3 rounded-lg border border-grey-30 font-schibsted text-[14px] text-black-0 focus:outline-none focus:border-grey-40"
                />
                <span className="font-schibsted text-[11px] text-grey-60">Distribute timestamps over this window (max 168)</span>
              </div>

              <div className="flex flex-col gap-1">
                <label className="font-schibsted font-medium text-[13px] text-black-0">
                  User Groups
                </label>
                <input
                  type="number"
                  min={0}
                  max={15}
                  value={config.userGroupCount}
                  onChange={(e) => handleChange("userGroupCount", e.target.value)}
                  disabled={!config.generateDirectory}
                  className="h-9 px-3 rounded-lg border border-grey-30 font-schibsted text-[14px] text-black-0 focus:outline-none focus:border-grey-40 disabled:opacity-50"
                />
                <span className="font-schibsted text-[11px] text-grey-60">Slack user groups like @eng, @design (max 15)</span>
              </div>
            </div>

            <div className="mt-2 flex flex-col gap-3">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={config.realisticTopicDistribution}
                  onChange={() => handleToggle("realisticTopicDistribution")}
                  className="w-4 h-4 accent-black-0"
                />
                <span className="font-schibsted text-[13px] text-black-0">
                  Realistic topic distribution (one dominant theme per channel)
                </span>
              </label>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="font-schibsted font-medium text-[13px] text-black-0">
                    Dominant topic share
                  </label>
                  <input
                    type="number"
                    min={0.15}
                    max={0.9}
                    step={0.05}
                    value={config.dominantTopicShare}
                    onChange={(e) => handleChangeFloat("dominantTopicShare", e.target.value)}
                    disabled={!config.realisticTopicDistribution}
                    className="h-9 px-3 rounded-lg border border-grey-30 font-schibsted text-[14px] text-black-0 focus:outline-none focus:border-grey-40 disabled:opacity-50"
                  />
                  <span className="font-schibsted text-[11px] text-grey-60">
                    Portion of messages per channel that repeat a dominant topic (e.g. 0.45 = 45%)
                  </span>
                </div>
              </div>
            </div>

            {/* Directory toggle */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={config.generateDirectory}
                onChange={() => handleToggle("generateDirectory")}
                className="w-4 h-4 accent-black-0"
              />
              <span className="font-schibsted text-[13px] text-black-0">
                Generate directory data (members with titles/roles, user groups, workspace profile)
              </span>
            </label>

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={loading || !organization}
              className="self-start h-9 px-4 flex items-center justify-center gap-2 rounded-lg bg-black-0 text-white font-schibsted font-medium text-[14px] leading-6 tracking-[-0.5px] hover:bg-grey-80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate mock data"
              )}
            </button>

            {/* Result feedback */}
            {result && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 flex flex-col gap-1">
                <p className="font-schibsted text-[13px] font-medium text-green-800">
                  {result.message}
                </p>
                <p className="font-schibsted text-[12px] text-green-700">
                  {result.data.memberCount} members across {result.data.channelCount} channels.
                  Events are unprocessed and will be picked up by the next processing cycle.
                </p>
                {result.data.directory && (
                  <p className="font-schibsted text-[12px] text-green-700">
                    Directory: {result.data.directory.membersUpserted} members with titles/roles, {result.data.directory.userGroupsUpserted} user groups, workspace profile updated.
                  </p>
                )}
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="font-schibsted text-[13px] text-red-700">{error}</p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default DevTools;
